import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  MenuItem,
  Grid,
  Divider,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface FeeHead {
  id: string;
  name: string;
  code: string;
}

interface FeeInstallment {
  id: string;
  name: string;
  sequenceOrder: number;
}

interface ClassModel {
  id: string;
  name: string;
}

interface CategoryModel {
  id: string;
  name: string;
}

interface FeeStructureLine {
  id?: string;
  feeHeadId: string;
  feeInstallmentId: string;
  amount: number;
  isActive: boolean;
  feeHead?: FeeHead;
  feeInstallment?: FeeInstallment;
}

interface FeeStructure {
  id: string;
  classId: string;
  categoryId?: string | null;
  isActive: boolean;
  class?: ClassModel;
  category?: CategoryModel;
  lines: FeeStructureLine[];
}

const FeeStructureMaster: React.FC = () => {
  const [structures, setStructures] = useState<FeeStructure[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Master Data
  const [classes, setClasses] = useState<ClassModel[]>([]);
  const [categories, setCategories] = useState<CategoryModel[]>([]);
  const [feeHeads, setFeeHeads] = useState<FeeHead[]>([]);
  const [installments, setInstallments] = useState<FeeInstallment[]>([]);

  // Form Data
  const [formData, setFormData] = useState<{
    classId: string;
    categoryId: string;
    isActive: boolean;
  }>({
    classId: '',
    categoryId: '',
    isActive: true,
  });

  // Matrix State: feeHeadId -> feeInstallmentId -> amount
  const [matrixData, setMatrixData] = useState<Record<string, Record<string, number>>>({});

  useEffect(() => {
    fetchMasterData();
    fetchStructures();
  }, []);

  const fetchMasterData = async () => {
    try {
      // Use new "active" endpoints where available to ensure full lists are returned
      const [cls, cats, heads, insts] = await Promise.all([
        axios.get('/system-settings/classes'), // Already sorted by sequenceOrder
        axios.get('/system-settings/category/active'), // New endpoint
        axios.get('/system-settings/fee-head/active'), // New endpoint
        axios.get('/system-settings/fee-installment'), // Already returns all
      ]);
      console.log(cls.data);
      setClasses(cls.data);
      setCategories(cats.data);
      setFeeHeads(heads.data);
      setInstallments(insts.data.sort((a: any, b: any) => a.sequenceOrder - b.sequenceOrder));
    } catch (err) {
      console.error('Failed to load master data', err);
      setError('Failed to load prerequisite master data');
    }
  };

  const fetchStructures = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/system-settings/fee-structure');
      setStructures(res.data);
    } catch (err) {
      setError('Failed to fetch fee structures');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setViewMode(false);
    setFormData({
      classId: '',
      categoryId: '',
      isActive: true,
    });
    setMatrixData({});
    setDialogOpen(true);
  };

  const handleEdit = (item: FeeStructure) => {
    setEditingId(item.id);
    setViewMode(false);
    setFormData({
      classId: item.classId,
      categoryId: item.categoryId || '',
      isActive: item.isActive,
    });

    // Populate Matrix from Lines
    const newMatrix: Record<string, Record<string, number>> = {};
    item.lines.forEach(line => {
      if (!newMatrix[line.feeHeadId]) {
        newMatrix[line.feeHeadId] = {};
      }
      newMatrix[line.feeHeadId][line.feeInstallmentId] = Number(line.amount);
    });
    setMatrixData(newMatrix);
    setDialogOpen(true);
  };

  const handleView = (item: FeeStructure) => {
    handleEdit(item);
    setViewMode(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fee structure? If it is in use, it will be deactivated instead.')) return;
    try {
      const res = await axios.delete(`/system-settings/fee-structure/${id}`);
      alert(res.data.message);
      fetchStructures();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Cannot delete fee structure');
    }
  };

  const handleMatrixChange = (headId: string, instId: string, value: string) => {
    const numValue = Number(value);
    setMatrixData(prev => ({
      ...prev,
      [headId]: {
        ...prev[headId],
        [instId]: numValue,
      },
    }));
  };

  const calculateMatrixTotal = () => {
    let total = 0;
    Object.values(matrixData).forEach(instMap => {
      Object.values(instMap).forEach(amount => {
        total += amount || 0;
      });
    });
    return total;
  };

  const handleSave = async () => {
    try {
      if (!formData.classId) {
        alert('Class is required');
        return;
      }

      // Convert Matrix to Lines
      const lines: { feeHeadId: string; feeInstallmentId: string; amount: number; isActive: boolean }[] = [];
      
      Object.entries(matrixData).forEach(([headId, instMap]) => {
        Object.entries(instMap).forEach(([instId, amount]) => {
          if (amount > 0) {
            lines.push({
              feeHeadId: headId,
              feeInstallmentId: instId,
              amount: amount,
              isActive: true,
            });
          }
        });
      });

      if (lines.length === 0) {
        alert('At least one fee amount must be greater than 0');
        return;
      }

      const payload = {
        classId: formData.classId,
        categoryId: formData.categoryId || undefined,
        isActive: formData.isActive,
        lines: lines,
      };

      if (editingId && !viewMode) {
        await axios.patch(`/system-settings/fee-structure/${editingId}`, payload);
      } else if (!editingId) {
        await axios.post('/system-settings/fee-structure', payload);
      }
      setDialogOpen(false);
      fetchStructures();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save fee structure');
    }
  };

  const calculateTotal = (lines: FeeStructureLine[]) => {
    return lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/fee-structure/${id}/toggle-status`);
      setStructures((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchStructures();
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Fee Structures</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create Structure
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Class</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : structures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No fee structures found.</TableCell>
              </TableRow>
            ) : (
              structures.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.class?.name}</TableCell>
                  <TableCell>{item.category?.name || 'All Categories'}</TableCell>
                  <TableCell>{calculateTotal(item.lines).toFixed(2)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(item)}><VisibilityIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(item)} title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item.id)} title="Delete (if unused)">
                        <DeleteIcon />
                      </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{viewMode ? 'View Fee Structure' : editingId ? 'Edit Fee Structure' : 'New Fee Structure'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Header Fields */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  label="Class"
                  value={formData.classId}
                  onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                  fullWidth
                  required
                  disabled={viewMode || !!editingId} // Prevent changing class on edit to avoid conflicts
                  helperText="Ordered by Academic Sequence"
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  label="Category (Optional)"
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  fullWidth
                  disabled={viewMode || !!editingId}
                  helperText="Leave empty for base structure"
                >
                  <MenuItem value=""><em>None (All Categories)</em></MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      disabled={viewMode}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />
            
            <Typography variant="subtitle1" gutterBottom>Fee Matrix</Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Fee Head \ Installment</TableCell>
                    {installments.map(inst => (
                      <TableCell key={inst.id} align="center" sx={{ fontWeight: 'bold' }}>
                        {inst.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeHeads.map(head => (
                    <TableRow key={head.id}>
                      <TableCell component="th" scope="row">
                        {head.name}
                      </TableCell>
                      {installments.map(inst => (
                        <TableCell key={`${head.id}-${inst.id}`} align="center">
                          <TextField
                            type="number"
                            size="small"
                            variant="outlined"
                            placeholder="0"
                            value={matrixData[head.id]?.[inst.id] || ''}
                            onChange={(e) => handleMatrixChange(head.id, inst.id, e.target.value)}
                            disabled={viewMode}
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={{ width: '80px' }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography variant="h6">Total Structure Amount: {calculateMatrixTotal().toFixed(2)}</Typography>
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {!viewMode && <Button onClick={handleSave} variant="contained">Save</Button>}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FeeStructureMaster;
